import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  forecastView,
  precipitationLabel,
  temperatureRangeLabel,
  weatherConditionLabel,
  weatherDayLabel,
  windLabel,
} from "@/lib/weatherPresentation";
import { Skeleton } from "@/components/ui/skeleton";

export function DestinationWeatherForecast({ destinationId, compact = false }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setForecast(null);
    setError(null);
    setLoading(true);

    api.get(`/parks/${destinationId}/weather`)
      .then(({ data }) => {
        if (active) setForecast(data);
      })
      .catch((requestError) => {
        if (active) setError(requestError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [destinationId]);

  const view = useMemo(() => forecastView({ loading, error, forecast }), [loading, error, forecast]);
  const visibleDays = view.kind === "ready" && compact ? view.days.slice(0, 4) : view.days;
  const dayGridClass = compact
    ? "grid grid-cols-2 gap-2"
    : "flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2 md:grid md:grid-cols-4 md:overflow-visible md:pb-0 xl:grid-cols-7";

  return (
    <div className={compact ? "mt-4" : "mt-5"} data-testid="destination-weather-forecast" data-weather-state={view.kind}>
      <div className={`${compact ? "mb-2" : "mb-3"} flex flex-wrap items-end justify-between gap-2`}>
        <div>
          <h3 className={`font-display font-bold uppercase text-white ${compact ? "text-lg" : "text-xl"}`}>
            {translateLabel(compact ? "Weather" : "7-day weather")}
          </h3>
          <p className="mt-1 text-xs text-zinc-500">{translateLabel("Forecast only.")}</p>
        </div>
        {forecast?.timezone && <span className="hidden text-xs text-zinc-500 sm:inline">{forecast.timezone}</span>}
      </div>

      {view.kind === "loading" && (
        <div className={dayGridClass} data-testid="weather-loading">
          {Array.from({ length: compact ? 4 : 7 }, (_, index) => (
            <Skeleton
              key={index}
              className={compact ? "h-24 rounded-xl bg-white/5" : "h-32 min-w-[13rem] snap-start rounded-xl bg-white/5 md:min-w-0"}
            />
          ))}
        </div>
      )}

      {view.kind === "error" && (
        <div className={`rounded-2xl border border-white/10 bg-[#121214] text-sm text-zinc-400 ${compact ? "p-4" : "p-5"}`} data-testid="weather-error">
          {translateLabel("Weather forecast is temporarily unavailable. Destination status and other details are still valid independently.")}
        </div>
      )}

      {view.kind === "empty" && (
        <div className={`rounded-2xl border border-white/10 bg-[#121214] text-sm text-zinc-400 ${compact ? "p-4" : "p-5"}`} data-testid="weather-empty">
          {translateLabel("No forecast data is available for this destination right now.")}
        </div>
      )}

      {view.kind === "ready" && (
        <div className={dayGridClass} data-testid="weather-days">
          {visibleDays.map((day, index) => (
            <article key={day.date} className={compact ? "rounded-xl border border-white/10 bg-[#121214] p-3" : "min-w-[13rem] snap-start rounded-xl border border-white/10 bg-[#121214] p-3 md:min-w-0"}>
              <div className="text-xs font-bold text-white">{compact && index === 0 ? translateLabel("Today") : weatherDayLabel(day.date)}</div>
              <div className={`${compact ? "mt-1" : "mt-2"} text-xs font-semibold text-[#CCFF00]`}>{weatherConditionLabel(day.condition)}</div>
              <div className={`${compact ? "mt-2 text-base" : "mt-3 text-lg"} font-display font-bold text-white`}>{temperatureRangeLabel(day)}</div>
              <dl className={`${compact ? "mt-2" : "mt-3"} space-y-1 text-[11px] text-zinc-400`}>
                <div className="flex justify-between gap-2"><dt>{translateLabel("Rain")}</dt><dd className="text-right text-zinc-300">{precipitationLabel(day)}</dd></div>
                <div className="flex justify-between gap-2"><dt>{translateLabel("Wind")}</dt><dd className="text-right text-zinc-300">{windLabel(day)}</dd></div>
              </dl>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
